const REPO = 'ottmartens/katariinamartens.ee';
const API_BASE = 'https://api.github.com';
const OAUTH_PROXY = 'https://decap-cms-github-oauth-api.ottmartens.workers.dev';

const TOKEN_KEY = 'github_editor_token';

export function getStoredToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(TOKEN_KEY);
}

export function storeToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

export async function validateToken(token: string): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/user`, {
      headers: { Authorization: `token ${token}` },
    });
    return res.ok;
  } catch {
    return false;
  }
}

export function initiateLogin(): Promise<string> {
  return new Promise((resolve, reject) => {
    const popup = window.open(
      `${OAUTH_PROXY}/auth`,
      'GitHub Login',
      'width=600,height=700'
    );

    if (!popup) {
      reject(new Error('Popup blocked'));
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      const data = event.data;

      if (data === 'authorizing:github') {
        popup!.postMessage('authorizing:github', event.origin);
        return;
      }

      let token: string | null = null;

      if (typeof data === 'string' && data.startsWith('authorization:github:success:')) {
        try {
          const json = JSON.parse(data.replace('authorization:github:success:', ''));
          token = json.token;
        } catch { /* ignore */ }
      } else if (typeof data === 'object' && data?.token) {
        token = data.token;
      }

      if (token) {
        window.removeEventListener('message', handleMessage);
        storeToken(token);
        resolve(token);
        popup.close();
      }
    };

    window.addEventListener('message', handleMessage);

    const checkClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkClosed);
        window.removeEventListener('message', handleMessage);
        reject(new Error('Login cancelled'));
      }
    }, 1000);
  });
}

export function logout() {
  clearToken();
}

// --- Git Data API: atomic batch commit ---

export type FileChange = {
  path: string;
  content: string;
  encoding: 'utf-8' | 'base64';
  deleted?: boolean;
};

function apiHeaders(token: string) {
  return {
    Authorization: `token ${token}`,
    'Content-Type': 'application/json',
  };
}

async function apiJson(url: string, options: RequestInit): Promise<any> {
  const res = await fetch(url, options);
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`GitHub API ${res.status}: ${body}`);
  }
  return res.json();
}

export async function commitAll(
  changes: FileChange[],
  message: string,
  token: string
): Promise<void> {
  if (changes.length === 0) return;

  const headers = apiHeaders(token);
  const base = `${API_BASE}/repos/${REPO}`;

  // 1. Get current HEAD
  const ref = await apiJson(`${base}/git/ref/heads/master`, { headers });
  const headSha: string = ref.object.sha;

  // 2. Get base tree SHA
  const headCommit = await apiJson(`${base}/git/commits/${headSha}`, { headers });
  const baseTreeSha: string = headCommit.tree.sha;

  // 3. Create blobs and build tree entries
  const treeEntries: any[] = [];

  for (const change of changes) {
    if (change.deleted) {
      treeEntries.push({
        path: change.path,
        mode: '100644',
        type: 'blob',
        sha: null,
      });
      continue;
    }

    const blob = await apiJson(`${base}/git/blobs`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        content: change.content,
        encoding: change.encoding,
      }),
    });

    treeEntries.push({
      path: change.path,
      mode: '100644',
      type: 'blob',
      sha: blob.sha,
    });
  }

  // 4. Create tree
  const newTree = await apiJson(`${base}/git/trees`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      base_tree: baseTreeSha,
      tree: treeEntries,
    }),
  });

  // 5. Create commit
  const newCommit = await apiJson(`${base}/git/commits`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      message,
      tree: newTree.sha,
      parents: [headSha],
    }),
  });

  // 6. Update ref to new commit
  await apiJson(`${base}/git/refs/heads/master`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ sha: newCommit.sha }),
  });
}

// Helper: convert ArrayBuffer to base64
export function arrayBufferToBase64(buffer: ArrayBuffer): string {
  return btoa(
    new Uint8Array(buffer).reduce((s, b) => s + String.fromCharCode(b), '')
  );
}

// Legacy single-file operations (kept for delete in toolbar)
export async function getFileContent(
  path: string,
  token: string
): Promise<{ content: string; sha: string }> {
  const data = await apiJson(`${API_BASE}/repos/${REPO}/contents/${path}`, {
    headers: apiHeaders(token),
  });
  const content = atob(data.content);
  return { content, sha: data.sha };
}

export async function deleteFile(
  path: string,
  sha: string,
  message: string,
  token: string
): Promise<void> {
  await apiJson(`${API_BASE}/repos/${REPO}/contents/${path}`, {
    method: 'DELETE',
    headers: apiHeaders(token),
    body: JSON.stringify({ message, sha }),
  });
}
